import { ref, computed, inject, type ComputedRef, type Ref } from 'vue'
import type { AxiosInstance } from 'axios'

import type { MetadataFilterRequest } from '@lib/types/core/MetadataFilter'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'

export interface FilterSuggestion {
  name: string
  displayName: string
  usageCount?: number
}

interface FilterSuggestionsData {
  suggestions: FilterSuggestion[]
  hasMore?: boolean
}

interface FilterConstraint {
  key: string
  value: string
}

interface UseFilterSuggestionsOptions {
  entity?: MetadataFilterRequest['entity']
  filterKey?: string
  includeDraftValues?: boolean
  isSection?: boolean
  isTemplate?: boolean
  ownerId?: number | null
  limit?: number
  tagsNotIn?: string[]
}

export default function useFilterSuggestions(options: UseFilterSuggestionsOptions = {}) {
  const pitcherInfo = inject<ComputedRef<{ instanceId: string }>>(
    'pitcherInfo',
    computed(() => ({ instanceId: '' })),
  )
  const coreRestApiAxios = inject<Ref<AxiosInstance | undefined>>('coreRestApiAxios')
  const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
    'pitcherSettings',
    computed(() => ({})),
  )

  // Reactive state
  const suggestionsData = ref<FilterSuggestionsData>({ suggestions: [] })
  const isLoading = ref(false)
  const isFetching = ref(false)
  const fetchTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

  // Check if suggestions feature is enabled
  const isEnabled = computed(
    () =>
      pitcherSettings.value.enable_search_filter_with_suggestions &&
      pitcherSettings.value.enable_quick_filters_for_search_metadata_filters,
  )

  /**
   * Build constraint array from current filters
   */
  function buildConstraints(currentFilters: Record<string, any>, excludeCurrentField = true): FilterConstraint[] {
    return Object.entries(currentFilters)
      .filter(([key, value]) => {
        // Skip empty values
        if (value === null || value === undefined || value === '') return false
        if (Array.isArray(value) && value.length === 0) return false

        // Skip current field if requested
        if (excludeCurrentField && key === options.filterKey) return false

        // Skip all system filters (they're not metadata fields and backend can't handle them)
        if (key.startsWith('system:')) return false

        return true
      })
      .map(([key, value]) => ({
        key,
        value: Array.isArray(value) ? value.join(',') : String(value),
      }))
  }

  /**
   * Fetch suggestions from API
   */
  async function fetchSuggestions(currentFilters: Record<string, any> = {}): Promise<FilterSuggestionsData> {
    if (!options.entity || !options.filterKey || !isEnabled.value) {
      return { suggestions: [] }
    }

    if (!coreRestApiAxios?.value) {
      console.error('Core REST API axios instance not available')
      return { suggestions: [] }
    }

    isLoading.value = true

    try {
      // Build constraints from current filters
      const constraints = buildConstraints(currentFilters)

      const response = await coreRestApiAxios.value.post<
        unknown,
        {
          results: Array<{ value: string; usage_count: number }>
          pagination: {
            cursor: string | null
            has_more: boolean
          }
        }
      >('protected/metadata-suggestions', {
        key: options.filterKey,
        entity: options.entity,
        instance_id: pitcherInfo.value.instanceId,
        is_section: options.isSection,
        is_template: options.isTemplate,
        include_draft_values: options.includeDraftValues,
        owner_id: options.ownerId?.toString(),
        page_size: options.limit || 100,
        and: constraints,
        tags__not_in: options.tagsNotIn?.length ? options.tagsNotIn.join(',') : undefined,
      })

      const result: FilterSuggestionsData = {
        suggestions:
          response.results?.map((item) => ({
            name: item.value,
            displayName: item.value,
            usageCount: item.usage_count,
          })) || [],
        hasMore: response.pagination?.has_more || false,
      }

      suggestionsData.value = result
      return result
    } catch (error) {
      console.error('Failed to fetch filter suggestions:', error)
      const emptyResult = { suggestions: [], hasMore: false }
      suggestionsData.value = emptyResult
      return emptyResult
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Debounced version of fetchSuggestions
   */
  function debouncedFetch(currentFilters: Record<string, any> = {}, delay = 150) {
    // Prevent concurrent requests
    if (isFetching.value) return Promise.resolve(suggestionsData.value)

    // Clear existing timeout
    if (fetchTimeout.value) {
      clearTimeout(fetchTimeout.value)
    }

    return new Promise<FilterSuggestionsData>((resolve) => {
      fetchTimeout.value = setTimeout(async () => {
        if (isFetching.value) {
          resolve(suggestionsData.value)
          return
        }

        isFetching.value = true
        try {
          const result = await fetchSuggestions(currentFilters)
          resolve(result)
        } finally {
          isFetching.value = false
        }
      }, delay)
    })
  }

  /**
   * Cleanup function
   */
  function cleanup() {
    if (fetchTimeout.value) {
      clearTimeout(fetchTimeout.value)
      fetchTimeout.value = null
    }
  }

  return {
    suggestionsData,
    isLoading,
    isEnabled,
    fetchSuggestions,
    debouncedFetch,
    buildConstraints,
    cleanup,
  }
}
